import React, { useState } from 'react';
import { signUp, confirmSignUp, signIn } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import PaymentForm from './PaymentForm';
import '../styles.css';

function Signup() {
  const navigate = useNavigate();
  const [step, setStep] = useState('signup'); // 'signup', 'confirm', 'payment-info'
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
  });
  const [confirmationCode, setConfirmationCode] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState(null);


  const handleSignup = async (e) => {
    e.preventDefault();
    setError(null);
    console.log('Signup attempt with:', formData);

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    try {
      await signUp({
        username: formData.email,
        password: formData.password,
        attributes: {
          email: formData.email,
          name: formData.fullName,
        },
        autoSignIn: { enabled: true },
      });
      console.log('Signup successful, moving to confirmation');
      setStep('confirm');
    } catch (error) {
      console.error('Signup error:', error);
      setError('Signup failed: ' + error.message);
    }
  };

  const handleResendCode = async () => {
    setError(null);
    try {
      await signUp({
        username: formData.email,
        password: formData.password,
        attributes: {
          email: formData.email,
          name: formData.fullName,
        },
        autoSignIn: { enabled: true },
      });
      console.log('Resent confirmation code to:', formData.email);
      setError('Confirmation code resent successfully.');
    } catch (error) {
      console.error('Resend code error:', error);
      setError('Failed to resend code: ' + error.message);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await confirmSignUp({ username: formData.email, confirmationCode });
      console.log('Email confirmed for:', formData.email);
      await signIn({ username: formData.email, password: formData.password });
      console.log('User signed in after confirmation:', formData.email);
      setStep('payment-info');
    } catch (error) {
      console.error('Confirmation error:', error);
      setError('Confirmation failed: ' + error.message);
    }
  };

  const handlePaymentSuccess = () => {
    console.log('Payment succeeded, redirecting to dashboard');
    navigate('/dashboard', { state: { fromSignup: true } });
  };

  return (
    <div>
      <Navbar showSignUpButton={false} />
      <div className="auth-container">
        {step === 'signup' && (
          <div id="signup-form" className="form-container">
            <h2>Sign Up</h2>
            <p className="subheader">Get better data to win your appeal</p>
            <form onSubmit={handleSignup}>
              <label htmlFor="full-name">Full Name</label>
              <input
                type="text"
                id="full-name"
                name="full-name"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                required
              />
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                onInput={() => setError(null)}
                required
              />
              <div style={{ margin: '10px 0', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="terms"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  style={{ margin: '3px 0 0 0', flexShrink: 0 }}
                />
                <label htmlFor="terms" style={{ display: 'inline', lineHeight: '1.4', fontSize: '14px' }}>
                  I accept the{' '}
                  <a href="/terms-and-conditions.pdf" target="_blank" rel="noopener noreferrer">
                    Terms and Conditions
                  </a>
                </label>
              </div>
              <button
                type="submit"
                disabled={!termsAccepted || !formData.fullName || !formData.email || !formData.password}
              >
                Next
              </button>
            </form>
            {error && (
              <div style={{ color: 'red', marginTop: '10px' }}>{error}</div>
            )}
            <p className="toggle-link">
              Already have an account? <a href="/login">Log in</a>
            </p>
          </div>
        )}
        {step === 'confirm' && (
          <div className="form-container">
            <h2>Confirm Your Email</h2>
            <p>A confirmation code has been sent to {formData.email}.</p>
            <form onSubmit={handleConfirm}>
              <label htmlFor="confirmation-code">Confirmation Code</label>
              <input
                type="text"
                id="confirmation-code"
                name="confirmation-code"
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value)}
                required
              />
              <button type="submit">Confirm</button>
            </form>
            <p style={{ marginTop: '10px' }}>
              Didn’t receive a code?{' '}
              <button
                onClick={handleResendCode}
                style={{ background: 'none', border: 'none', color: '#007BFF', cursor: 'pointer' }}
              >
                Resend
              </button>
            </p>
            {error && (
              <div style={{ color: error.includes('successfully') ? 'green' : 'red', marginTop: '10px' }}>
                {error}
              </div>
            )}
          </div>
        )}
        {step === 'payment-info' && (
          <div className="form-container">
            <h2>Go Premium — $79 for 5 Reports</h2>
            <p className="subheader">Unlock 5 full property analyses, market trend charts, and property tax appeal reports.</p>
            <PaymentForm
              userId={formData.email}
              onSuccess={handlePaymentSuccess}
              setError={setError}
            />
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => navigate('/dashboard', { state: { fromSignup: true } })}
                style={{ background: 'none', border: 'none', color: '#0ea5e9', cursor: 'pointer', fontSize: '15px', textDecoration: 'underline' }}
              >
                Skip for now & start my free search →
              </button>
            </div>
            {error && (
              <div style={{ color: 'red', marginTop: '10px' }}>{error}</div>
            )}
          </div>
        )}
      </div>
      <footer className="footer">
        <p>
          © 2025 Casalytix. All rights reserved. | <a href="/privacy-policy" className="footer-link">Privacy Policy</a>
        </p>
      </footer>
    </div>
  );
}

export default Signup;